/**
 * 単位の一覧（`/units`）。名前だけを全件出し、表し方は名前の title に回す。
 *
 * 単位は数える型の用語（金額・量）が指す語の集合である。一覧の問いは「どの単位が使えるか
 * （目当ての単位を開く）」なので、表し方（整数・小数・日時）は名前の補足として title に置き、
 * 詳細の欄でも読める（Dhik 2026-09-29「本当に出すべき情報を 1 or 2 個に」）。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 単位を名前だけで全件一覧し、表し方を名前の title に添えてマスター詳細へ繋ぐ。
 * @graph-connects useListMasters [calls] 単位一覧の取得
 * @graph-connects MasterTableListPage [calls] 名前の一覧の本体
 */
import { useSearch } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';

import { useListMasters } from '../hooks/queries/spec-graph-element-master-queries.js';
import { MasterTableListPage } from './master-table-list.js';
import { pjTextOf } from './pj-search.js';
import { UNITS_ROUTE } from './route-paths.js';

/**
 * @graph-node {Function}
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 単位の一覧を、名前の 1 列の表と、名前の title の表し方で描く。
 * @graph-connects useListMasters [calls] 単位一覧の取得
 * @graph-connects pjTextOf [calls] 検索条件の案件番号を文字列へ戻す
 */
export function UnitsPage() {
  const { t } = useTranslation();
  const pj = pjTextOf(useSearch({ from: UNITS_ROUTE }).pj);
  const { data, isLoading, error } = useListMasters('unit', pj);

  return (
    <MasterTableListPage
      type="unit"
      titleKey="units.title"
      nameKey="units.columnName"
      emptyKey="units.empty"
      hint={(item) =>
        t('units.notationHint', {
          notation:
            item.notation === undefined || item.notation === ''
              ? t('masterDetail.unset')
              : item.notation,
        })
      }
      items={data?.items}
      isLoading={isLoading}
      error={error}
      pj={pj}
    />
  );
}
